import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import MainCard from '../components/MainCard';
import PageHeader from '../components/PageHeader';
import PageLoader from '../components/PageLoader';

type Monitor = {
  name: string;
  status: number;
  monitor_type: string;
  attribute_label?: string;
  attribute_value?: string | number;
  unit?: string;
  last_polled_time?: string;
  down_reason?: string;
  duration?: string;
  monitor_id: string;
  tags?: string[];
};

type MonitorsCount = {
  maintenance: number;
  up: number;
  down: number;
  critical: number;
  trouble: number;
  discovery: number;
  suspended: number;
  configuration_error: number;
  total: number;
};

export function Site24x7Page() {
  const { hasPageAccess } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [monitors, setMonitors] = useState<Monitor[]>([]);
  const [counts, setCounts] = useState<MonitorsCount | null>(null);
  
  const truncate = (text: string | undefined, max: number) =>
    text && text.length > max ? `${text.slice(0, max - 1)}…` : (text ?? '');

  useEffect(() => {
    if (!hasPageAccess('monitoring')) return;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.get('/monitoring/site24x7/current-status');
        const outer = resp.data?.data ?? {};
        const data = outer?.data ?? outer;
        setMonitors(Array.isArray(data?.monitors) ? data.monitors : []);
        setCounts(data?.monitors_count || null);
      } catch (e: any) {
        setError('Failed to load Site24x7 status');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [hasPageAccess]);

  const grouped = useMemo(() => {
    const byStatus: Record<number, Monitor[]> = {};
    for (const m of monitors) {
      byStatus[m.status] = byStatus[m.status] || [];
      byStatus[m.status].push(m);
    }
    return byStatus;
  }, [monitors]);

  const sortedMonitors = useMemo(() => {
    const failing: Monitor[] = [];
    const passing: Monitor[] = [];
    
    for (const m of monitors) {
      if (m.status === 0 || m.status === 2 || m.status === 3) {
        failing.push(m);
      } else if (m.status === 1) {
        passing.push(m);
      }
    }
    
    failing.sort((a, b) => {
      const priority = { 3: 0, 0: 1, 2: 2 };
      return (priority[a.status as keyof typeof priority] ?? 99) - (priority[b.status as keyof typeof priority] ?? 99);
    });
    
    const byType: Record<string, Monitor[]> = {};
    for (const m of passing) {
      const type = m.monitor_type || 'Unknown';
      if (!byType[type]) {
        byType[type] = [];
      }
      byType[type].push(m);
    }
    
    const sortedTypes = Object.keys(byType).sort();
    const sortedPassing: Monitor[] = [];
    for (const type of sortedTypes) {
      byType[type].sort((a, b) => a.name.localeCompare(b.name));
      sortedPassing.push(...byType[type]);
    }
    
    return [...failing, ...sortedPassing];
  }, [monitors]);

  const statusBadge = (status: number) => {
    switch (status) {
      case 1:
        return <Badge variant="success">Up</Badge>;
      case 2:
        return <Badge variant="warning">Trouble</Badge>;
      case 0:
        return <Badge variant="destructive">Down</Badge>;
      case 3:
        return <Badge variant="destructive">Critical</Badge>;
      default:
        return <Badge variant="secondary">Unknown</Badge>;
    }
  };

  if (loading) {
    return <PageLoader message="Loading Site24x7 monitors..." />;
  }

  return (
    <div>
      <PageHeader
        title="Site24x7 Monitor Status"
        subtitle="Live status of monitors from Site24x7"
      />

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <MainCard>
          <span className="text-xs text-muted-foreground">Up</span>
          <p className="text-2xl font-semibold text-emerald-600 dark:text-emerald-400">
            {counts?.up ?? (grouped[1]?.length || 0)}
          </p>
        </MainCard>
        <MainCard>
          <span className="text-xs text-muted-foreground">Trouble</span>
          <p className="text-2xl font-semibold text-amber-600 dark:text-amber-400">
            {counts?.trouble ?? (grouped[2]?.length || 0)}
          </p>
        </MainCard>
        <MainCard>
          <span className="text-xs text-muted-foreground">Down</span>
          <p className="text-2xl font-semibold text-red-600 dark:text-red-400">
            {counts?.down ?? (grouped[0]?.length || 0)}
          </p>
        </MainCard>
        <MainCard>
          <span className="text-xs text-muted-foreground">Total</span>
          <p className="text-2xl font-semibold">
            {counts?.total ?? monitors.length}
          </p>
        </MainCard>
      </div>

      <MainCard contentClassName="!p-0">
        <div className="overflow-x-auto">
          <Table className="min-w-[1200px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Name</TableHead>
                <TableHead className="w-[18%]">Type</TableHead>
                <TableHead className="w-[100px]">Status</TableHead>
                <TableHead className="w-[24%]">Metric</TableHead>
                <TableHead className="w-64">Last Polled</TableHead>
                <TableHead className="w-[30%]">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : sortedMonitors.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    No monitors found.
                  </TableCell>
                </TableRow>
              ) : (
                sortedMonitors.map((m, index) => {
                  const prev = index > 0 ? sortedMonitors[index - 1] : null;
                  const isFailing = m.status === 0 || m.status === 2 || m.status === 3;
                  const prevIsFailing = prev ? (prev.status === 0 || prev.status === 2 || prev.status === 3) : false;
                  const showFailingHeader = index === 0 && isFailing;
                  const showPassingHeader = prevIsFailing && !isFailing;
                  const showTypeHeader = !isFailing && (showPassingHeader || (prev && !prevIsFailing && prev.monitor_type !== m.monitor_type));
                  
                  const metric =
                    m.attribute_label && (m.attribute_value ?? m.attribute_value === 0)
                      ? `${m.attribute_label}: ${m.attribute_value}${m.unit ? ` ${m.unit}` : ''}`
                      : '-';
                  
                  return (
                    <Fragment key={m.monitor_id}>
                      {showFailingHeader && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-red-50 dark:bg-red-950/30 font-semibold text-red-800 dark:text-red-300 border-t-2 border-red-500">
                            Issues (Down / Trouble / Critical)
                          </TableCell>
                        </TableRow>
                      )}
                      {showPassingHeader && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-emerald-50 dark:bg-emerald-950/30 font-semibold text-emerald-800 dark:text-emerald-300 border-t-2 border-emerald-500">
                            Healthy Monitors
                          </TableCell>
                        </TableRow>
                      )}
                      {showTypeHeader && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-muted/50 font-medium border-t border-border">
                            {m.monitor_type}
                          </TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell className="font-medium" title={m.name}>
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {truncate(m.name, 42)}
                          </div>
                        </TableCell>
                        <TableCell title={m.monitor_type}>
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {m.monitor_type}
                          </div>
                        </TableCell>
                        <TableCell>{statusBadge(m.status)}</TableCell>
                        <TableCell title={metric}>
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {metric}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap" title={m.last_polled_time || '-'}>
                          <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                            {m.last_polled_time || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {m.down_reason ? (
                            <div className="overflow-hidden text-ellipsis whitespace-nowrap text-red-600 dark:text-red-400" title={m.down_reason}>
                              {m.down_reason}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </MainCard>
    </div>
  );
}
